/**
 * TrainingLocations — /training-locations
 *
 * Stage-2 P2 (Feb 18, 2026) — Training Location Intelligence MVP.
 *
 * Operator brief:
 *   "Give instructors a simple way to find useful nearby training
 *    locations for the skill they are teaching. Assistive only —
 *    not full route intelligence. 'may help', 'nearby option',
 *    'use if appropriate'."
 *
 * Doctrine:
 *   • Read-only. No actions, no resends, no auto-routing.
 *   • Calm slate palette + amber chips per complexity level (no red).
 *   • Single endpoint, single source of truth.
 *   • Empty / prompt states are explicit + calm.
 *   • No flashing, no map embed, no analytics scoring.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Compass, MapPin, ExternalLink, Search, AlertCircle, Layers,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const RADII = [1, 3, 5, 10, 20];

function _isAppleDevice() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod|Macintosh/i.test(navigator.userAgent || "");
}


function LevelChip({ level }) {
  // Slate → slate → amber-50 → amber-100. Never red.
  const tone = level >= 4
    ? "bg-amber-100 text-amber-900 border-amber-200"
    : level >= 3
    ? "bg-amber-50 text-amber-800 border-amber-200"
    : level >= 2
    ? "bg-slate-100 text-slate-800 border-slate-200"
    : "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <span
      data-testid={`training-location-level-chip-${level}`}
      className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border ${tone}`}
    >
      <Layers className="w-3 h-3 opacity-70" />
      Level {level}
    </span>
  );
}


function LocationCard({ item }) {
  const hint = item.maps_deeplink_hint;
  const mapsUrl = hint
    ? (_isAppleDevice() ? hint.apple_url : hint.google_url)
    : null;
  return (
    <div
      data-testid={`training-location-card-${item.id}`}
      className="rounded-xl border border-slate-200 bg-white p-4"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <LevelChip level={item.level} />
            <span className="text-[11px] text-slate-500">
              {String(item.location_type).replace(/_/g, " ")}
            </span>
          </div>
          <div className="text-sm font-semibold text-slate-900 truncate">
            {item.name}
          </div>
        </div>
        {mapsUrl ? (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`training-location-maps-${item.id}`}
            className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-700 hover:underline underline-offset-2 flex-none"
          >
            <ExternalLink className="w-3.5 h-3.5 opacity-70" />
            Open in Maps
          </a>
        ) : null}
      </div>

      <p className="text-[13px] text-slate-700 leading-relaxed">
        {item.why_it_may_help}
      </p>

      {item.cautions ? (
        <p
          data-testid={`training-location-caution-${item.id}`}
          className="mt-2 text-[12px] text-slate-500 leading-relaxed flex items-start gap-1.5"
        >
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-none opacity-60" />
          {item.cautions}
        </p>
      ) : null}
    </div>
  );
}


function LevelSection({ levelKey, levelLabel, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={`training-location-section-${levelKey}`} className="mb-6">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">
        {levelLabel}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((item) => <LocationCard key={item.id} item={item} />)}
      </div>
    </div>
  );
}


export default function TrainingLocations() {
  const [skills, setSkills] = useState([]);
  const [postcode, setPostcode] = useState("");
  const [radius, setRadius] = useState(5);
  const [skill, setSkill] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/training-locations/skills`,
          { withCredentials: true },
        );
        setSkills(data.skills || []);
      } catch (_) { setSkills([]); }
    })();
  }, []);

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(
        `${API}/api/training-locations/suggest`,
        {
          params: { skill, postcode, radius },
          withCredentials: true,
        },
      );
      setData(data);
    } catch (e) {
      setError(
        e?.response?.data?.detail
          || e?.message
          || "Couldn't load suggestions right now. Try again shortly.",
      );
    } finally {
      setLoading(false);
    }
  }, [skill, postcode, radius]);

  // Auto-fetch on mount (calm prompt envelope when fields blank).
  useEffect(() => { fetchSuggestions(); }, [fetchSuggestions]);

  const totalMatched = data?.matched ?? 0;
  const groupKeys = useMemo(
    () => Object.keys(data?.groups || {}),
    [data],
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="training-locations-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex items-start gap-3 mb-2">
          <Compass className="w-5 h-5 mt-1 text-slate-500 flex-none" />
          <div>
            <h1
              className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight"
              data-testid="training-locations-title"
            >
              Training-location ideas
            </h1>
            <p
              className="text-sm text-slate-600 mt-1 max-w-2xl"
              data-testid="training-locations-subtitle"
            >
              Calm suggestions of nearby place-types that may help
              practise a skill. Assistive only — use if appropriate.
              The platform does not memorise test routes.
            </p>
          </div>
        </div>

        {/* Inputs */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 mt-6 mb-6 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <label className="md:col-span-4 block">
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
              Postcode / area
            </span>
            <input
              type="text"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="SW1A 1AA"
              data-testid="training-locations-postcode-input"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-slate-400"
            />
          </label>
          <label className="md:col-span-2 block">
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
              Radius (mi)
            </span>
            <select
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              data-testid="training-locations-radius-select"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
            >
              {RADII.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className="md:col-span-4 block">
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
              What are you teaching today?
            </span>
            <select
              value={skill}
              onChange={(e) => setSkill(e.target.value)}
              data-testid="training-locations-skill-select"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white"
            >
              <option value="">Choose a skill…</option>
              {skills.map((s) => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </label>
          <div className="md:col-span-2">
            <button
              type="button"
              onClick={fetchSuggestions}
              data-testid="training-locations-search-btn"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
            >
              <Search className="w-4 h-4" />
              Show ideas
            </button>
          </div>
        </div>

        {/* States */}
        {error ? (
          <div
            data-testid="training-locations-error"
            className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm mb-6"
          >
            {error}
          </div>
        ) : null}

        {data?.prompt ? (
          <div
            data-testid="training-locations-prompt"
            className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 mb-6 flex items-start gap-2"
          >
            <MapPin className="w-4 h-4 mt-0.5 text-slate-500 flex-none" />
            <p className="text-sm text-slate-700">{data.prompt}</p>
          </div>
        ) : null}

        {!data?.prompt && totalMatched === 0 && !loading ? (
          <div
            data-testid="training-locations-empty"
            className="rounded-xl border border-slate-200 bg-white px-4 py-8 mb-6 text-center"
          >
            <Layers className="w-7 h-7 mx-auto text-slate-300 mb-2" />
            <div className="text-sm font-semibold text-slate-700">
              No ideas yet for that combination.
            </div>
            <div className="text-[12px] text-slate-500 mt-1">
              Try a different skill or widen the radius.
            </div>
          </div>
        ) : null}

        {totalMatched > 0 ? (
          <div data-testid="training-locations-results">
            <div className="text-[12px] text-slate-500 mb-3">
              {totalMatched} idea{totalMatched === 1 ? "" : "s"} —
              {" "}
              <span className="text-slate-600">assistive only · use if appropriate</span>
            </div>
            {groupKeys.map((key) => (
              <LevelSection
                key={key}
                levelKey={key}
                levelLabel={data.level_labels?.[key.replace("level_", "")] || key}
                items={data.groups[key]}
              />
            ))}
          </div>
        ) : null}

        <p className="text-[11px] text-slate-400 mt-6 text-center">
          Suggestions are assistive only. Use only if appropriate for
          the learner. The platform does not memorise test routes.
        </p>
      </div>
    </div>
  );
}
